<template>
  <header class="header" :class="{ 'sidebar-collapsed': props.sidebarCollapsed }">
    <div class="header-top">
      <div class="header-left">
        <button class="sidebar-toggle" @click="$emit('toggle-sidebar')">
          <i class="fas fa-bars"></i>
        </button>
        <el-divider direction="vertical" class="toggle-divider" />
        <AppBreadcrumb />
      </div>
      <div class="header-right">
        <!-- Dil Seçici -->
        <el-dropdown trigger="click" class="language-dropdown" @command="handleLanguageChange">
          <button class="language-btn">
            <span class="flag-icon">{{ currentLanguageData?.flag || '🇹🇷' }}</span>
            <span class="lang-code">{{ currentLanguageData?.code?.toUpperCase() || 'TR' }}</span>
            <i class="fas fa-chevron-down"></i>
          </button>
          <template #dropdown>
            <el-dropdown-menu class="language-menu">
              <el-dropdown-item 
                v-for="lang in languages" 
                :key="lang.code" 
                :command="lang.code"
                :class="{ 'is-active': lang.code === currentLanguage }"
              >
                <span class="flag-icon">{{ lang.flag }}</span>
                <span class="lang-name">{{ lang.nativeName }}</span>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <!-- Bildirim İkonu -->
        <el-dropdown trigger="click" class="notification-dropdown">
          <el-badge :value="totalNotifications" :hidden="totalNotifications === 0" :max="99" type="danger">
            <button class="header-icon-btn">
              <i class="fas fa-bell"></i>
            </button>
          </el-badge>
          <template #dropdown>
            <el-dropdown-menu class="notification-menu">
              <div class="notification-header">
                <span>{{ t('header.notifications', 'Bildirimler') }}</span>
              </div>
              <el-dropdown-item @click="$router.push('/birthdays?filter=today')">
                <div class="notification-item">
                  <div class="notification-icon birthday">
                    <i class="fas fa-birthday-cake"></i>
                  </div>
                  <div class="notification-content">
                    <span class="notification-title">{{ t('header.todayBirthday', 'Bugün Doğum Günü') }}</span>
                    <span class="notification-count">{{ birthdayCount }} {{ t('common.person', 'kişi') }}</span>
                  </div>
                </div>
              </el-dropdown-item>
              <el-dropdown-item @click="$router.push('/reports/daily-attendance?filter=absent')">
                <div class="notification-item">
                  <div class="notification-icon absent">
                    <i class="fas fa-user-times"></i>
                  </div>
                  <div class="notification-content">
                    <span class="notification-title">{{ t('header.todayAbsent', 'Bugün Gelmeyenler') }}</span>
                    <span class="notification-count">{{ absentCount }} {{ t('common.person', 'kişi') }}</span>
                  </div>
                </div>
              </el-dropdown-item>
              <el-dropdown-item @click="$router.push('/leave-requests-list?status=pending')">
                <div class="notification-item">
                  <div class="notification-icon leave">
                    <i class="fas fa-clock"></i>
                  </div>
                  <div class="notification-content">
                    <span class="notification-title">{{ t('header.pendingLeaveRequest', 'Bekleyen İzin Talebi') }}</span>
                    <span class="notification-count">{{ pendingLeaveCount }} {{ t('header.request', 'talep') }}</span>
                  </div>
                </div>
              </el-dropdown-item>
              <el-dropdown-item @click="$router.push('/advance-requests-list?status=pending')">
                <div class="notification-item">
                  <div class="notification-icon advance">
                    <i class="fas fa-money-bill-wave"></i>
                  </div>
                  <div class="notification-content">
                    <span class="notification-title">{{ t('header.pendingAdvanceRequest', 'Bekleyen Avans Talebi') }}</span>
                    <span class="notification-count">{{ pendingAdvanceCount }} {{ t('header.request', 'talep') }}</span>
                  </div>
                </div>
              </el-dropdown-item>
              <el-dropdown-item @click="$router.push('/events')">
                <div class="notification-item">
                  <div class="notification-icon event">
                    <i class="fas fa-calendar-check"></i>
                  </div>
                  <div class="notification-content">
                    <span class="notification-title">{{ t('header.upcomingEvent', 'Yaklaşan Etkinlik') }}</span>
                    <span class="notification-count">{{ eventCount }} {{ t('header.event', 'etkinlik') }}</span>
                  </div>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>

        <!-- Ayarlar İkonu -->
        <el-tooltip :content="t('header.settings', 'Ayarlar')" placement="bottom">
          <button class="header-icon-btn" @click="$router.push('/settings')">
            <i class="fas fa-cog"></i>
          </button>
        </el-tooltip>

        <!-- Kullanıcı İkonu -->
        <el-dropdown @command="handleCommand" trigger="click" class="user-dropdown">
          <button class="header-icon-btn user-btn">
            <div class="avatar">{{ userInitials }}</div>
            <i class="fas fa-chevron-down user-chevron"></i>
          </button>
          <template #dropdown>
            <el-dropdown-menu class="user-menu">
              <div class="user-info-header">
                <div class="avatar-large">{{ userInitials }}</div>
                <div class="user-details">
                  <span class="user-name">{{ userName }}</span>
                  <span class="user-email">{{ auth.user?.email }}</span>
                </div>
              </div>
              <el-dropdown-item v-if="isSubuser" command="change-password" divided>
                <i class="fas fa-key"></i>
                {{ t('header.changePassword', 'Şifre Değiştir') }}
              </el-dropdown-item>
              <el-dropdown-item v-else command="profile" divided>
                <i class="fas fa-user-edit"></i>
                {{ t('header.editProfile', 'Profil Düzenle') }}
              </el-dropdown-item>
              <el-dropdown-item command="logout">
                <i class="fas fa-sign-out-alt"></i>
                {{ t('header.logout', 'Çıkış Yap') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useLanguageStore } from '../stores/language'
import { storeToRefs } from 'pinia'
import { computed, ref, onMounted } from 'vue'
import AppBreadcrumb from './AppBreadcrumb.vue'
import api from '@/api'

const props = defineProps<{
  sidebarCollapsed?: boolean
}>()

defineEmits(['toggle-sidebar'])

const router = useRouter()
const auth = useAuthStore()
const languageStore = useLanguageStore()
const { currentLanguage, languages } = storeToRefs(languageStore)
const { t, setLanguage, fetchLanguages, fetchTranslations } = languageStore

// Bildirim sayıları
const birthdayCount = ref(0)
const eventCount = ref(0)
const absentCount = ref(0)
const pendingLeaveCount = ref(0)
const pendingAdvanceCount = ref(0)

const totalNotifications = computed(() => birthdayCount.value + eventCount.value + absentCount.value + pendingLeaveCount.value + pendingAdvanceCount.value)

const userName = computed(() => {
  return auth.user?.name || t('header.user', 'Kullanıcı')
})

const userInitials = computed(() => {
  const name = auth.user?.name || ''
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

const isSubuser = computed(() => {
  return auth.isSubuser()
})

const handleCommand = (command: string) => {
  switch (command) {
    case 'profile':
      router.push('/profile/edit')
      break
    case 'change-password':
      router.push('/change-password')
      break
    case 'logout':
      handleLogout()
      break
  }
}

const handleLogout = () => {
  auth.logout()
  router.push('/login')
}

// Language - storeToRefs ile reactive
const currentLanguageData = computed(() => {
  return languages.value.find(l => l.code === currentLanguage.value) || null
})

const handleLanguageChange = (langCode: string) => {
  setLanguage(langCode)
}

// Bildirim verilerini çek
const fetchNotificationCounts = async () => {
  try {
    const companyId = auth.user?.company_id || auth.user?.id
    if (!companyId) return

    const today = new Date().toISOString().split('T')[0]

    // Bugünün doğum günlerini çek
    const birthdayResponse = await api.get(`/employees/birthdays/${companyId}`, {
      params: { date: today }
    })
    birthdayCount.value = birthdayResponse.data?.length || 0

    // Bugün gelmeyenleri çek
    const attendanceResponse = await api.get('/attendance/daily-report', {
      params: { date: today, company_id: companyId }
    })
    absentCount.value = attendanceResponse.data?.summary?.todayNotAttended || 0

    // Bekleyen izin taleplerini çek
    const leaveResponse = await api.get('/leave-requests/company', {
      params: { company_id: companyId, status: 'pending' }
    })
    pendingLeaveCount.value = leaveResponse.data?.length || 0

    // Bekleyen avans taleplerini çek
    const advanceResponse = await api.post('/advance-requests/company', {
      params: { company_id: companyId, status: 'pending' }
    })
    pendingAdvanceCount.value = advanceResponse.data?.length || 0

    // Yaklaşan etkinlikleri çek
    const eventResponse = await api.get(`/events/upcoming/${companyId}`, {
      params: { days: 7 }
    })
    eventCount.value = eventResponse.data?.length || 0
  } catch (error) {
    console.error('Error fetching notification counts:', error)
  }
}

onMounted(async () => {
  // Dilleri ve çevirileri yükle
  await fetchLanguages()
  await fetchTranslations()
  
  // Bildirim sayılarını yükle
  fetchNotificationCounts()
})
</script>

<style scoped>
.header {
  background: rgba(2, 6, 23, 0.85);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  left: 240px;
  right: 0;
  z-index: 100;
  border-bottom: 1px solid rgba(148, 163, 184, 0.08);
  border-radius: 0;
  padding: 0 !important;
  transition: left 0.3s ease;
}

.header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(ellipse at 30% 0%, rgba(56, 189, 248, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 100%, rgba(249, 115, 22, 0.04) 0%, transparent 50%);
  pointer-events: none;
  border-radius: 0;
}

.header.sidebar-collapsed {
  left: 64px;
}

.header-top {
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 0px;
  width: 100%;
  position: relative;
  z-index: 1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.toggle-divider {
  height: 20px;
  margin: 0 4px;
  border-color: rgba(148, 163, 184, 0.2);
}

.sidebar-toggle {
  background: none;
  border: none;
  color: #e2e8f0;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;
}

.sidebar-toggle:hover {
  background: rgba(148, 163, 184, 0.1);
  color: #ffffff;
}

/* Header Icon Button */
.header-icon-btn {
  background: none;
  border: none;
  color: #cbd5e1;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 16px;
  transition: all 0.2s;
}

.header-icon-btn:hover {
  background: rgba(148, 163, 184, 0.1);
  color: #ffffff;
}

.header-icon-btn.user-btn {
  padding: 4px 8px;
  gap: 8px;
}

.user-chevron {
  font-size: 10px;
  color: #94a3b8;
}

/* Avatar */
.avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: rgba(148, 163, 184, 0.2);
  color: #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.avatar-large {
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

/* Notification Dropdown */
.notification-dropdown :deep(.el-badge__content) {
  font-size: 10px;
  top: 4px;
  right: 14px;
}

/* User Dropdown */
.user-dropdown :deep(.el-dropdown-menu) {
  min-width: 240px;
}

/* User Info Header in Dropdown */
.user-info-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 0px solid #e5e7eb;
  margin-bottom: 4px;
  min-width: 240px;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
}

.user-email {
  font-size: 12px;
  color: #6b7280;
}

/* Notification Header */
.notification-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 4px;
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
}

/* Notification Item */
.notification-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}

.notification-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.notification-icon.birthday {
  background: linear-gradient(135deg, #fecaca 0%, #fca5a5 100%);
  color: #dc2626;
}

.notification-icon.event {
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  color: #2563eb;
}

.notification-icon.absent {
  background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%);
  color: #ea580c;
}

.notification-icon.leave {
  background: linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%);
  color: #7c3aed;
}

.notification-icon.advance {
  background: linear-gradient(135deg, #a7f3d0 0%, #6ee7b7 100%);
  color: #059669;
}

.notification-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notification-title {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.notification-count {
  font-size: 12px;
  color: #6b7280;
}

/* Language Selector */
.language-dropdown {
  margin-right: 4px;
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(148, 163, 184, 0.1);
  cursor: pointer;
  font-size: 12px;
  color: #e2e8f0;
  transition: all 0.2s;
  border: 1px solid rgba(148, 163, 184, 0.15);
}

.language-btn:hover {
  background: rgba(148, 163, 184, 0.2);
  border-color: rgba(148, 163, 184, 0.25);
}

.language-btn .flag-icon {
  font-size: 16px;
  line-height: 1;
}

.language-btn .lang-code {
  font-weight: 600;
  font-size: 11px;
}

.language-btn .fa-chevron-down {
  font-size: 9px;
  color: #94a3b8;
  margin-left: 2px;
}

/* Language Menu */
:deep(.language-menu) {
  min-width: 160px;
}

:deep(.language-menu .el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
}

:deep(.language-menu .el-dropdown-menu__item .flag-icon) {
  font-size: 18px;
}

:deep(.language-menu .el-dropdown-menu__item .lang-name) {
  font-size: 13px;
}

:deep(.language-menu .el-dropdown-menu__item.is-active) {
  background: #e6effd;
  color: #4a90e2;
  font-weight: 600;
}

@media (max-width: 768px) {
  .header {
    left: 64px;
  }
}
</style> 